/**
 * Follow-up Management Page
 * Dynamic campaign-based follow-up for lead lists via WhatsApp & Telegram
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Clock, Send, Users, TrendingUp, Calendar, Play, Pause,
    Edit, Eye, CheckCircle, XCircle, AlertCircle, RefreshCw,
    MessageSquare, Plus, Trash2, Target, Zap, X, Save,
    ChevronDown, ChevronUp, BarChart2, Settings
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const api = {
    async get(endpoint) {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, { headers: getAuthHeaders() });
        if (!res.ok) throw new Error(`API Error ${res.status}: ${res.statusText}`);
        return res.json();
    },
    async post(endpoint, data) {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error(`API Error ${res.status}: ${res.statusText}`);
        return res.json();
    },
    async put(endpoint, data) {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error(`API Error ${res.status}: ${res.statusText}`);
        return res.json();
    },
    async patch(endpoint, data) {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error(`API Error ${res.status}: ${res.statusText}`);
        return res.json();
    },
    async delete(endpoint) {
        const res = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'DELETE',
            headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error(`API Error ${res.status}: ${res.statusText}`);
        return res.json();
    },
};

// ==================== CONSTANTS ====================
const LEAD_STATUSES = ['NEW', 'CONTACTED', 'QUALIFIED', 'NURTURING', 'VIEWING_SCHEDULED', 'NEGOTIATING'];
const LEAD_SOURCES = ['whatsapp', 'telegram', 'linkedin', 'manual', 'referral'];
const CHANNELS = ['whatsapp', 'telegram'];

const PLACEHOLDER_HELP = `Available placeholders:
{name} - Lead's full name
{company} - Lead's company
{phone} - Lead's phone number
{email} - Lead's email`;

// ==================== SUB-COMPONENTS ====================

const StatCard = ({ title, value, icon: Icon, color = 'gold', trend }) => (
    <div className="glass-card glass-card-hover rounded-2xl p-6">
        <div className="flex items-start justify-between mb-4">
            <p className="text-sm font-medium text-white/50 uppercase tracking-wider">{title}</p>
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-500/20`}>
                <Icon className={`w-6 h-6 text-${color}-500`} />
            </div>
        </div>
        <h3 className="text-3xl font-bold text-white">{value}</h3>
        {trend && <p className="text-sm text-green-400 mt-2">{trend}</p>}
    </div>
);

const StatusBadge = ({ active, status }) => {
    if (active) {
        return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-500/20 text-green-400 border border-green-500/30">🟢 ACTIVE</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-gray-500/20 text-gray-400 border border-gray-500/30">⏸ PAUSED</span>;
};

// Campaign Card
const CampaignCard = ({ campaign, onEdit, onDelete, onToggle, onPreview }) => {
    const [previewCount, setPreviewCount] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);

    const handlePreview = async () => {
        setLoadingPreview(true);
        try {
            const data = await api.get(`/api/followup/campaigns/${campaign.id}/preview-count`);
            setPreviewCount(data.total_matching_leads);
        } catch (e) {
            setPreviewCount('Error');
        } finally {
            setLoadingPreview(false);
        }
    };

    return (
        <div className="glass-card rounded-xl p-5 border border-white/10">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h4 className="text-white font-semibold text-base truncate">{campaign.name}</h4>
                        <StatusBadge active={campaign.active} status={campaign.status} />
                    </div>
                    {campaign.description && (
                        <p className="text-gray-400 text-xs">{campaign.description}</p>
                    )}
                </div>
                <div className="flex gap-1 ml-2">
                    <button onClick={handlePreview} title="Preview lead count"
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-blue-400">
                        <Eye className="w-4 h-4" />
                    </button>
                    <button onClick={() => onEdit(campaign)} title="Edit"
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gold-400">
                        <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => onToggle(campaign)} title={campaign.active ? 'Pause' : 'Activate'}
                        className={`p-2 hover:bg-white/10 rounded-lg transition-colors ${campaign.active ? 'text-orange-400' : 'text-green-400'}`}>
                        {campaign.active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                    <button onClick={() => onDelete(campaign)} title="Delete"
                        className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400">
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Preview Count */}
            {(previewCount !== null || loadingPreview) && (
                <div className="mb-3 px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-300 flex items-center gap-2">
                    <Target className="w-4 h-4 flex-shrink-0" />
                    {loadingPreview ? 'Counting matching leads...' : `${previewCount} leads currently match this campaign`}
                </div>
            )}

            {/* Message Preview */}
            <div className="p-3 bg-navy-800/60 rounded-lg border border-white/5 mb-4">
                <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" /> Message Template
                </p>
                <p className="text-sm text-gray-300 italic line-clamp-3">{campaign.message_template}</p>
            </div>

            {/* Config Details */}
            <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 bg-white/5 rounded-lg">
                    <span className="text-gray-500 block mb-0.5">First Send After</span>
                    <span className="text-white font-medium">{campaign.delay_hours_after_import}h</span>
                </div>
                <div className="p-2 bg-white/5 rounded-lg">
                    <span className="text-gray-500 block mb-0.5">Repeat Every</span>
                    <span className="text-white font-medium">{campaign.repeat_interval_hours}h</span>
                </div>
                <div className="p-2 bg-white/5 rounded-lg">
                    <span className="text-gray-500 block mb-0.5">Max Per Lead</span>
                    <span className="text-white font-medium">{campaign.max_sends_per_lead} msgs</span>
                </div>
                <div className="p-2 bg-white/5 rounded-lg">
                    <span className="text-gray-500 block mb-0.5">Channels</span>
                    <span className="text-white font-medium">{(campaign.channels || []).join(', ')}</span>
                </div>
            </div>

            {/* Target Filters */}
            <div className="mt-3 flex flex-wrap gap-1">
                {(campaign.target_status || []).map(s => (
                    <span key={s} className="px-2 py-0.5 bg-gold-500/10 text-gold-400 border border-gold-500/20 rounded text-xs">{s}</span>
                ))}
                {(campaign.target_sources || []).map(s => (
                    <span key={s} className="px-2 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded text-xs">{s}</span>
                ))}
                {(campaign.target_tags || []).map(t => (
                    <span key={t} className="px-2 py-0.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded text-xs">#{t}</span>
                ))}
            </div>

            {/* Stats */}
            <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-4 gap-1 text-xs text-center">
                <div><div className="text-white font-bold">{campaign.total_targeted}</div><div className="text-gray-500">Targeted</div></div>
                <div><div className="text-white font-bold">{campaign.total_sent}</div><div className="text-gray-500">Sent</div></div>
                <div><div className="text-white font-bold">{campaign.total_delivered}</div><div className="text-gray-500">Delivered</div></div>
                <div><div className="text-green-400 font-bold">{campaign.total_replied}</div><div className="text-gray-500">Replied</div></div>
            </div>
        </div>
    );
};

// Campaign Form Modal
const CampaignModal = ({ campaign, onClose, onSave }) => {
    const isEdit = !!campaign?.id;
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        name: campaign?.name || '',
        description: campaign?.description || '',
        message_template: campaign?.message_template || '',
        target_status: campaign?.target_status || [],
        target_sources: campaign?.target_sources || [],
        target_tags: campaign?.target_tags || [],
        min_score: campaign?.min_score ?? '',
        max_score: campaign?.max_score ?? '',
        delay_hours_after_import: campaign?.delay_hours_after_import ?? 1,
        repeat_interval_hours: campaign?.repeat_interval_hours ?? 72,
        max_sends_per_lead: campaign?.max_sends_per_lead ?? 5,
        send_window_start: campaign?.send_window_start ?? '',
        send_window_end: campaign?.send_window_end ?? '',
        channels: campaign?.channels || ['whatsapp'],
        active: campaign?.active ?? true,
    });
    const [tagsInput, setTagsInput] = useState((campaign?.target_tags || []).join(', '));

    const toggleArrayItem = (field, value) => {
        setForm(f => ({
            ...f,
            [field]: f[field].includes(value)
                ? f[field].filter(v => v !== value)
                : [...f[field], value]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { setError('Campaign name is required'); return; }
        if (!form.message_template.trim()) { setError('Message template is required'); return; }
        setSaving(true);
        setError(null);
        try {
            const payload = {
                ...form,
                target_tags: tagsInput.split(',').map(t => t.trim()).filter(Boolean),
                min_score: form.min_score !== '' ? parseInt(form.min_score) : null,
                max_score: form.max_score !== '' ? parseInt(form.max_score) : null,
                delay_hours_after_import: parseFloat(form.delay_hours_after_import),
                repeat_interval_hours: parseFloat(form.repeat_interval_hours),
                max_sends_per_lead: parseInt(form.max_sends_per_lead),
                send_window_start: form.send_window_start !== '' ? parseInt(form.send_window_start) : null,
                send_window_end: form.send_window_end !== '' ? parseInt(form.send_window_end) : null,
            };
            let result;
            if (isEdit) {
                result = await api.put(`/api/followup/campaigns/${campaign.id}`, payload);
            } else {
                result = await api.post('/api/followup/campaigns', payload);
            }
            onSave(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
            <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto glass-card rounded-2xl p-6 shadow-2xl">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Zap className="w-5 h-5 text-gold-500" />
                        {isEdit ? 'Edit Campaign' : 'Create Follow-up Campaign'}
                    </h2>
                    <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                        <X className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                {error && (
                    <div className="mb-4 px-4 py-3 bg-red-500/20 border border-red-500/40 rounded-lg text-red-400 text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Basic Info */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-gold-400 uppercase tracking-wider flex items-center gap-2">
                            <Settings className="w-4 h-4" /> Campaign Info
                        </h3>
                        <div>
                            <label className="text-gray-400 text-sm block mb-1">Campaign Name *</label>
                            <input type="text" value={form.name}
                                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                className="w-full bg-navy-800 text-white rounded-lg px-4 py-2.5 border border-white/10 focus:border-gold-500 outline-none text-sm"
                                placeholder="e.g. New WhatsApp Lead Warm-up" />
                        </div>
                        <div>
                            <label className="text-gray-400 text-sm block mb-1">Description (optional)</label>
                            <input type="text" value={form.description}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                className="w-full bg-navy-800 text-white rounded-lg px-4 py-2.5 border border-white/10 focus:border-gold-500 outline-none text-sm"
                                placeholder="Brief campaign description" />
                        </div>
                    </div>

                    {/* Message Template */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-gold-400 uppercase tracking-wider flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" /> Message Template *
                        </h3>
                        <textarea
                            value={form.message_template}
                            onChange={e => setForm(f => ({ ...f, message_template: e.target.value }))}
                            rows={5}
                            className="w-full bg-navy-800 text-white rounded-lg px-4 py-3 border border-white/10 focus:border-gold-500 outline-none text-sm resize-none"
                            placeholder={`Hi {name}! 👋\n\nI noticed you're interested in Dubai real estate...\n\nWould you like to explore investment opportunities?`}
                        />
                        <p className="text-xs text-gray-500 bg-navy-900/50 p-2 rounded whitespace-pre">{PLACEHOLDER_HELP}</p>
                    </div>

                    {/* Target Filters */}
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gold-400 uppercase tracking-wider flex items-center gap-2">
                            <Target className="w-4 h-4" /> Target List Filters
                        </h3>
                        <p className="text-xs text-gray-500">Select which leads this campaign targets. Empty = all leads.</p>

                        <div>
                            <label className="text-gray-400 text-xs block mb-2">Lead Status</label>
                            <div className="flex flex-wrap gap-2">
                                {LEAD_STATUSES.map(s => (
                                    <button key={s} type="button"
                                        onClick={() => toggleArrayItem('target_status', s)}
                                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-all border ${form.target_status.includes(s)
                                            ? 'bg-gold-500/20 text-gold-400 border-gold-500/40'
                                            : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30'}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="text-gray-400 text-xs block mb-2">Lead Source</label>
                            <div className="flex flex-wrap gap-2">
                                {LEAD_SOURCES.map(s => (
                                    <button key={s} type="button"
                                        onClick={() => toggleArrayItem('target_sources', s)}
                                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-all border capitalize ${form.target_sources.includes(s)
                                            ? 'bg-blue-500/20 text-blue-400 border-blue-500/40'
                                            : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30'}`}>
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div>
                            <label className="text-gray-400 text-xs block mb-1">Tags (comma-separated)</label>
                            <input type="text" value={tagsInput}
                                onChange={e => setTagsInput(e.target.value)}
                                className="w-full bg-navy-800 text-white rounded-lg px-4 py-2.5 border border-white/10 focus:border-gold-500 outline-none text-sm"
                                placeholder="e.g. hot, investor, golden_visa" />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-gray-400 text-xs block mb-1">Min Score (0-100)</label>
                                <input type="number" min="0" max="100" value={form.min_score}
                                    onChange={e => setForm(f => ({ ...f, min_score: e.target.value }))}
                                    className="w-full bg-navy-800 text-white rounded-lg px-3 py-2 border border-white/10 focus:border-gold-500 outline-none text-sm"
                                    placeholder="0" />
                            </div>
                            <div>
                                <label className="text-gray-400 text-xs block mb-1">Max Score (0-100)</label>
                                <input type="number" min="0" max="100" value={form.max_score}
                                    onChange={e => setForm(f => ({ ...f, max_score: e.target.value }))}
                                    className="w-full bg-navy-800 text-white rounded-lg px-3 py-2 border border-white/10 focus:border-gold-500 outline-none text-sm"
                                    placeholder="100" />
                            </div>
                        </div>
                    </div>

                    {/* Timing */}
                    <div className="space-y-3">
                        <h3 className="text-sm font-semibold text-gold-400 uppercase tracking-wider flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Timing Configuration
                        </h3>
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="text-gray-400 text-xs block mb-1">First send after (hours)</label>
                                <input type="number" min="0.5" step="0.5" value={form.delay_hours_after_import}
                                    onChange={e => setForm(f => ({ ...f, delay_hours_after_import: e.target.value }))}
                                    className="w-full bg-navy-800 text-white rounded-lg px-3 py-2 border border-white/10 focus:border-gold-500 outline-none text-sm" />
                            </div>
                            <div>
                                <label className="text-gray-400 text-xs block mb-1">Repeat every (hours)</label>
                                <input type="number" min="1" step="1" value={form.repeat_interval_hours}
                                    onChange={e => setForm(f => ({ ...f, repeat_interval_hours: e.target.value }))}
                                    className="w-full bg-navy-800 text-white rounded-lg px-3 py-2 border border-white/10 focus:border-gold-500 outline-none text-sm" />
                            </div>
                            <div>
                                <label className="text-gray-400 text-xs block mb-1">Max sends per lead</label>
                                <input type="number" min="1" max="20" value={form.max_sends_per_lead}
                                    onChange={e => setForm(f => ({ ...f, max_sends_per_lead: e.target.value }))}
                                    className="w-full bg-navy-800 text-white rounded-lg px-3 py-2 border border-white/10 focus:border-gold-500 outline-none text-sm" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-gray-400 text-xs block mb-1">Send window start (UTC hour, optional)</label>
                                <input type="number" min="0" max="23" value={form.send_window_start}
                                    onChange={e => setForm(f => ({ ...f, send_window_start: e.target.value }))}
                                    className="w-full bg-navy-800 text-white rounded-lg px-3 py-2 border border-white/10 focus:border-gold-500 outline-none text-sm"
                                    placeholder="e.g. 9 (= 9am)" />
                            </div>
                            <div>
                                <label className="text-gray-400 text-xs block mb-1">Send window end (UTC hour, optional)</label>
                                <input type="number" min="0" max="23" value={form.send_window_end}
                                    onChange={e => setForm(f => ({ ...f, send_window_end: e.target.value }))}
                                    className="w-full bg-navy-800 text-white rounded-lg px-3 py-2 border border-white/10 focus:border-gold-500 outline-none text-sm"
                                    placeholder="e.g. 18 (= 6pm)" />
                            </div>
                        </div>
                    </div>

                    {/* Channels */}
                    <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-gold-400 uppercase tracking-wider flex items-center gap-2">
                            <Send className="w-4 h-4" /> Channels
                        </h3>
                        <div className="flex gap-3">
                            {CHANNELS.map(ch => (
                                <button key={ch} type="button"
                                    onClick={() => toggleArrayItem('channels', ch)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border capitalize ${form.channels.includes(ch)
                                        ? 'bg-gold-500/20 text-gold-400 border-gold-500/40'
                                        : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/30'}`}>
                                    {ch === 'whatsapp' ? '📱 WhatsApp' : '✈️ Telegram'}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Active toggle */}
                    <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                        <div>
                            <p className="text-white text-sm font-medium">Activate Campaign</p>
                            <p className="text-gray-500 text-xs mt-0.5">When active, engine will process this campaign every hour</p>
                        </div>
                        <button type="button" onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                            className={`relative w-12 h-6 rounded-full transition-colors ${form.active ? 'bg-gold-500' : 'bg-gray-600'}`}>
                            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${form.active ? 'left-7' : 'left-1'}`} />
                        </button>
                    </div>

                    {/* Form Actions */}
                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose}
                            className="flex-1 px-4 py-2.5 bg-white/10 text-white hover:bg-white/20 rounded-xl transition-colors text-sm">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving}
                            className="flex-1 px-4 py-2.5 btn-gold rounded-xl flex items-center justify-center gap-2 text-sm disabled:opacity-50">
                            {saving
                                ? <div className="w-4 h-4 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" />
                                : <><Save className="w-4 h-4" />{isEdit ? 'Save Changes' : 'Create Campaign'}</>
                            }
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// FollowupLeadCard (fixed: name not full_name, safe source access)
const FollowupLeadCard = ({ lead, onSendManual, onUpdateSchedule }) => {
    const isOverdue = lead.next_followup_at && new Date(lead.next_followup_at) <= new Date();
    const statusColors = {
        NEW: 'bg-red-500/15 text-red-400 border-red-500/30',
        CONTACTED: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
        QUALIFIED: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
        NURTURING: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    };

    // Fix: API returns 'name' not 'full_name'
    const displayName = lead.name || lead.full_name || 'Anonymous';
    const displaySource = (lead.source || 'unknown').toUpperCase();

    return (
        <div className="glass-card glass-card-hover rounded-xl p-4">
            <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-500 to-gold-600 flex items-center justify-center text-navy-900 font-bold flex-shrink-0">
                    {displayName.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <h4 className="text-white font-medium text-sm">{displayName}</h4>
                        {lead.lead_score > 0 && (
                            <span className="text-xs font-bold text-gold-500 bg-gold-500/10 px-2 py-0.5 rounded">{lead.lead_score}</span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded border font-medium ${statusColors[lead.status] || 'bg-gray-500/15 text-gray-400 border-gray-500/30'}`}>
                            {lead.status}
                        </span>
                    </div>
                    <p className="text-gray-500 text-xs">{lead.phone || lead.email || displaySource}</p>
                </div>
            </div>

            <div className="space-y-1.5 mb-3 p-3 bg-white/5 rounded-xl border border-white/10 text-xs">
                <div className="flex justify-between">
                    <span className="text-gray-400">Next Follow-up:</span>
                    <span className={`font-medium ${isOverdue ? 'text-red-400' : 'text-white'}`}>
                        {lead.next_followup_at ? new Date(lead.next_followup_at).toLocaleString() : 'Not scheduled'}
                        {isOverdue && ' ⏰ OVERDUE'}
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-400">Follow-ups sent:</span>
                    <span className="text-white font-medium">{lead.followup_count} / 5</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-400">Last Contact:</span>
                    <span className="text-white font-medium">
                        {lead.last_contacted_at ? new Date(lead.last_contacted_at).toLocaleDateString() : 'Never'}
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-400">Source:</span>
                    <span className="text-white font-medium">{displaySource}</span>
                </div>
            </div>

            <div className="flex gap-2">
                <button onClick={() => onSendManual(lead)}
                    className="flex-1 px-3 py-2 btn-gold rounded-lg flex items-center justify-center gap-1.5 text-xs">
                    <Send className="w-3 h-3" /> Send Now
                </button>
                <button onClick={() => onUpdateSchedule(lead)}
                    className="flex-1 px-3 py-2 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg flex items-center justify-center gap-1.5 text-xs transition-colors">
                    <Calendar className="w-3 h-3" /> Reschedule
                </button>
            </div>
        </div>
    );
};

// ==================== TEMPLATE EDITOR CARD ====================

const STAGE_EMOJIS = ['👋', '💎', '⚡', '📩', '🙏'];
const STAGE_COLORS = [
    'from-blue-500/20 to-blue-600/10 border-blue-500/30',
    'from-gold-500/20 to-gold-600/10 border-gold-500/30',
    'from-orange-500/20 to-orange-600/10 border-orange-500/30',
    'from-red-500/20 to-red-600/10 border-red-500/30',
    'from-purple-500/20 to-purple-600/10 border-purple-500/30',
];

const TemplateCard = ({ template, onSave, onReset, saving }) => {
    const [editing, setEditing] = useState(false);
    const [lang, setLang] = useState('en');
    const [form, setForm] = useState({
        content_en: template.content_en || '',
        content_fa: template.content_fa || '',
        content_ar: template.content_ar || '',
        is_active: template.is_active,
    });

    // Sync if template prop changes
    useEffect(() => {
        setForm({
            content_en: template.content_en || '',
            content_fa: template.content_fa || '',
            content_ar: template.content_ar || '',
            is_active: template.is_active,
        });
    }, [template]);

    const langLabels = { en: '🇬🇧 English', fa: '🇮🇷 فارسی', ar: '🇦🇪 العربية' };
    const currentContent = template[`content_${lang}`];
    const colorClass = STAGE_COLORS[template.stage] || STAGE_COLORS[0];

    const handleSave = () => {
        onSave(template.stage, form);
        setEditing(false);
    };

    const handleCancel = () => {
        setForm({
            content_en: template.content_en || '',
            content_fa: template.content_fa || '',
            content_ar: template.content_ar || '',
            is_active: template.is_active,
        });
        setEditing(false);
    };

    return (
        <div className={`glass-card rounded-xl border bg-gradient-to-br ${colorClass} overflow-hidden`}>
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">{STAGE_EMOJIS[template.stage]}</span>
                    <div>
                        <div className="flex items-center gap-2">
                            <h4 className="text-white font-semibold text-sm">Stage {template.stage}: {template.stage_name}</h4>
                            {template.is_custom && (
                                <span className="px-1.5 py-0.5 bg-gold-500/20 text-gold-400 border border-gold-500/30 rounded text-xs">Custom</span>
                            )}
                            {!form.is_active && (
                                <span className="px-1.5 py-0.5 bg-gray-500/20 text-gray-400 border border-gray-500/30 rounded text-xs">Disabled</span>
                            )}
                        </div>
                        {template.updated_at && (
                            <p className="text-gray-500 text-xs mt-0.5">Last saved: {new Date(template.updated_at).toLocaleDateString()}</p>
                        )}
                    </div>
                </div>
                <div className="flex gap-1">
                    {template.is_custom && !editing && (
                        <button onClick={() => onReset(template.stage)} title="Reset to default"
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-red-400 text-xs flex items-center gap-1">
                            <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                    )}
                    {!editing ? (
                        <button onClick={() => setEditing(true)} title="Edit template"
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gold-400 flex items-center gap-1">
                            <Edit className="w-4 h-4" />
                        </button>
                    ) : (
                        <>
                            <button onClick={handleCancel}
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400">
                                <X className="w-4 h-4" />
                            </button>
                            <button onClick={handleSave} disabled={saving}
                                className="px-3 py-1.5 btn-gold rounded-lg flex items-center gap-1.5 text-xs disabled:opacity-50">
                                {saving ? <div className="w-3 h-3 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                Save
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Language Selector */}
            <div className="px-4 pb-0 flex gap-1">
                {['en', 'fa', 'ar'].map(l => (
                    <button key={l} onClick={() => setLang(l)}
                        className={`px-3 py-1 rounded-t-lg text-xs font-medium transition-all ${lang === l ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
                        {langLabels[l]}
                    </button>
                ))}
                {editing && (
                    <div className="ml-auto flex items-center gap-2 pb-1">
                        <label className="text-gray-500 text-xs">Active</label>
                        <button onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                            className={`relative w-9 h-5 rounded-full transition-colors ${form.is_active ? 'bg-gold-500' : 'bg-gray-600'}`}>
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${form.is_active ? 'left-4' : 'left-0.5'}`} />
                        </button>
                    </div>
                )}
            </div>

            {/* Content Area */}
            <div className="px-4 pb-4">
                <div className="p-3 bg-navy-900/60 rounded-b-xl rounded-tr-xl border border-white/5">
                    {editing ? (
                        <textarea
                            value={form[`content_${lang}`]}
                            onChange={e => setForm(f => ({ ...f, [`content_${lang}`]: e.target.value }))}
                            rows={6}
                            dir={lang !== 'en' ? 'rtl' : 'ltr'}
                            className="w-full bg-transparent text-white text-sm outline-none resize-none leading-relaxed"
                            placeholder={`Write your ${langLabels[lang]} message here...\n\nAvailable: {name}, {company}, {phone}, {email}`}
                        />
                    ) : (
                        <p className={`text-sm text-gray-300 whitespace-pre-wrap leading-relaxed ${lang !== 'en' ? 'text-right' : ''}`}
                            dir={lang !== 'en' ? 'rtl' : 'ltr'}>
                            {currentContent || <span className="text-gray-600 italic">No content for this language yet. Click edit to add.</span>}
                        </p>
                    )}
                </div>
                {editing && (
                    <p className="text-xs text-gray-600 mt-1.5">
                        Placeholders: <code className="text-gold-500/70">{'{name}'}</code> <code className="text-gold-500/70">{'{company}'}</code> <code className="text-gold-500/70">{'{phone}'}</code>
                    </p>
                )}
            </div>
        </div>
    );
};

// ==================== MAIN COMPONENT ====================

const FollowupManagement = () => {
    const [activeTab, setActiveTab] = useState('campaigns');
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingLeadId, setSendingLeadId] = useState(null);
    const [pendingOnly, setPendingOnly] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCampaign, setEditingCampaign] = useState(null);
    const [toast, setToast] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [savingTemplateStage, setSavingTemplateStage] = useState(null);

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [statsData, leadsData, campaignsData, templatesData] = await Promise.all([
                api.get('/api/followup/stats').catch(() => null),
                api.get(`/api/followup/leads?pending_only=${pendingOnly}`).catch(() => []),
                api.get('/api/followup/campaigns').catch(() => []),
                api.get('/api/followup/templates').catch(() => []),
            ]);
            if (statsData) setStats(statsData);
            setLeads(Array.isArray(leadsData) ? leadsData : []);
            setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
            setTemplates(Array.isArray(templatesData) ? templatesData : []);
        } catch (err) {
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [pendingOnly]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSaveCampaign = (saved) => {
        setCampaigns(prev => {
            const idx = prev.findIndex(c => c.id === saved.id);
            return idx >= 0 ? prev.map(c => c.id === saved.id ? saved : c) : [saved, ...prev];
        });
        setShowModal(false);
        setEditingCampaign(null);
        showToast(`Campaign "${saved.name}" ${editingCampaign?.id ? 'updated' : 'created'} successfully!`);
    };

    const handleDeleteCampaign = async (campaign) => {
        if (!confirm(`Delete campaign "${campaign.name}"?`)) return;
        try {
            await api.delete(`/api/followup/campaigns/${campaign.id}`);
            setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
            showToast(`Campaign deleted.`);
        } catch (err) {
            showToast(`Error: ${err.message}`, 'error');
        }
    };

    const handleToggleCampaign = async (campaign) => {
        try {
            const updated = await api.put(`/api/followup/campaigns/${campaign.id}`, { active: !campaign.active });
            setCampaigns(prev => prev.map(c => c.id === campaign.id ? updated : c));
            showToast(`Campaign ${updated.active ? 'activated' : 'paused'}.`);
        } catch (err) {
            showToast(`Error: ${err.message}`, 'error');
        }
    };

    const handleSendManual = async (lead) => {
        if (!confirm(`Send follow-up to ${lead.name || lead.full_name}?`)) return;
        setSendingLeadId(lead.id);
        try {
            const result = await api.post('/api/followup/manual', { lead_id: lead.id });
            if (result.success) {
                showToast(`✅ Sent via ${result.channel}`);
                fetchData();
            } else {
                showToast(`❌ ${result.message}`, 'error');
            }
        } catch (err) {
            showToast(`Error: ${err.message}`, 'error');
        } finally {
            setSendingLeadId(null);
        }
    };

    const handleUpdateSchedule = async (lead) => {
        const hours = prompt(`Reschedule for ${lead.name || 'lead'}.\nEnter hours from now (0 to disable):`, '24');
        if (hours === null) return;
        try {
            const h = parseInt(hours);
            await api.patch('/api/followup/schedule', {
                lead_id: lead.id,
                next_followup_at: h > 0 ? new Date(Date.now() + h * 3600000).toISOString() : null,
                followup_enabled: h > 0,
            });
            showToast('Schedule updated!');
            fetchData();
        } catch (err) {
            showToast(`Error: ${err.message}`, 'error');
        }
    };

    const handleRunEngine = async () => {
        if (!confirm('Run follow-up engine now? This will send pending messages.')) return;
        try {
            await api.post('/api/followup/test-engine', {});
            showToast('✅ Engine executed! Refresh to see results.');
            fetchData();
        } catch (err) {
            showToast(`Error: ${err.message}`, 'error');
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center h-96">
                <div className="w-12 h-12 border-4 border-navy-800 border-t-gold-500 rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6 animate-fade-in">
            {/* Toast */}
            {toast && (
                <div className={`fixed top-6 right-6 z-50 px-5 py-3 rounded-xl shadow-xl text-sm font-medium transition-all ${toast.type === 'error' ? 'bg-red-500/90 text-white' : 'bg-green-500/90 text-white'}`}>
                    {toast.msg}
                </div>
            )}

            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                        <MessageSquare className="w-8 h-8 text-gold-500" />
                        Follow-up Management
                    </h1>
                    <p className="text-gray-400">Automated lead nurturing across Telegram & WhatsApp</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={fetchData} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 transition-colors text-sm">
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                    <button onClick={handleRunEngine} className="px-4 py-2 btn-gold rounded-xl flex items-center gap-2 text-sm">
                        <Play className="w-4 h-4" /> Run Engine Now
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard title="Active Campaigns" value={campaigns.filter(c => c.active).length} icon={Zap} color="gold" />
                <StatCard title="Scheduled Leads" value={stats?.total_scheduled || 0} icon={Users} color="blue" />
                <StatCard title="Sent Today" value={stats?.sent_today || 0} icon={Send} color="green" />
                <StatCard title="Success Rate" value={`${stats?.success_rate || 0}%`} icon={TrendingUp} color="gold"
                    trend={stats?.pending > 0 ? `⏰ ${stats.pending} pending` : '✅ All caught up'} />
            </div>

            {/* Tabs */}
            <div className="flex bg-navy-800/60 rounded-xl p-1 gap-1 border border-white/10">
                {[
                    { id: 'campaigns', label: 'Campaigns', icon: Zap },
                    { id: 'templates', label: 'Message Templates', icon: MessageSquare },
                    { id: 'leads', label: `Leads (${leads.length})`, icon: Users },
                ].map(tab => (
                    <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-gold-500 text-navy-900' : 'text-gray-400 hover:text-white'}`}>
                        <tab.icon className="w-4 h-4" /> {tab.label}
                    </button>
                ))}
            </div>

            {/* Campaigns Tab */}
            {activeTab === 'campaigns' && (
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-white">Follow-up Campaigns</h2>
                        <button onClick={() => { setEditingCampaign(null); setShowModal(true); }}
                            className="px-4 py-2 btn-gold rounded-xl flex items-center gap-2 text-sm">
                            <Plus className="w-4 h-4" /> New Campaign
                        </button>
                    </div>

                    {campaigns.length === 0 ? (
                        <div className="glass-card rounded-2xl p-12 text-center">
                            <Zap className="w-12 h-12 text-gold-500/40 mx-auto mb-4" />
                            <p className="text-gray-400 text-lg mb-2">No campaigns yet</p>
                            <p className="text-gray-500 text-sm mb-6">Create your first follow-up campaign to automate lead nurturing</p>
                            <button onClick={() => { setEditingCampaign(null); setShowModal(true); }}
                                className="px-6 py-3 btn-gold rounded-xl inline-flex items-center gap-2">
                                <Plus className="w-4 h-4" /> Create First Campaign
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            {campaigns.map(c => (
                                <CampaignCard key={c.id} campaign={c}
                                    onEdit={camp => { setEditingCampaign(camp); setShowModal(true); }}
                                    onDelete={handleDeleteCampaign}
                                    onToggle={handleToggleCampaign}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
                <div className="space-y-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-white">Follow-up Message Templates</h2>
                            <p className="text-gray-500 text-sm mt-0.5">
                                Edit the default message for each stage. Supports Persian, English and Arabic.
                                Use <code className="text-gold-400">{'{name}'}</code>, <code className="text-gold-400">{'{company}'}</code> as placeholders.
                            </p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {templates.length === 0 ? (
                            <div className="col-span-2 text-center py-12 text-gray-500">Loading templates...</div>
                        ) : templates.map(tpl => (
                            <TemplateCard
                                key={tpl.stage}
                                template={tpl}
                                saving={savingTemplateStage === tpl.stage}
                                onSave={async (stage, data) => {
                                    setSavingTemplateStage(stage);
                                    try {
                                        await api.put(`/api/followup/templates/${stage}`, data);
                                        // Refresh templates
                                        const updated = await api.get('/api/followup/templates').catch(() => templates);
                                        setTemplates(Array.isArray(updated) ? updated : templates);
                                        showToast(`✅ Stage ${stage} template saved!`);
                                    } catch (err) {
                                        showToast(`❌ ${err.message}`, 'error');
                                    } finally {
                                        setSavingTemplateStage(null);
                                    }
                                }}
                                onReset={async (stage) => {
                                    if (!confirm(`Reset Stage ${stage} to default text?`)) return;
                                    try {
                                        await api.delete(`/api/followup/templates/${stage}`);
                                        const updated = await api.get('/api/followup/templates').catch(() => templates);
                                        setTemplates(Array.isArray(updated) ? updated : templates);
                                        showToast(`Stage ${stage} reset to default.`);
                                    } catch (err) {
                                        showToast(`❌ ${err.message}`, 'error');
                                    }
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Leads Tab */}
            {activeTab === 'leads' && (
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <button onClick={() => setPendingOnly(!pendingOnly)}
                            className={`px-4 py-2 rounded-xl flex items-center gap-2 text-sm transition-colors ${pendingOnly
                                ? 'bg-gold-500/20 text-gold-400 border border-gold-500/30'
                                : 'bg-white/10 text-white hover:bg-white/20'}`}>
                            <Clock className="w-4 h-4" />
                            {pendingOnly ? 'Pending Only' : 'All Scheduled'}
                        </button>
                        <span className="text-sm text-gray-400">{leads.length} lead(s)</span>
                    </div>

                    {leads.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">
                            {pendingOnly ? '✅ No pending follow-ups! All caught up.' : 'No leads with follow-up scheduled.'}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {leads.map(lead => (
                                <FollowupLeadCard key={lead.id} lead={lead}
                                    onSendManual={handleSendManual}
                                    onUpdateSchedule={handleUpdateSchedule}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Campaign Modal */}
            {showModal && (
                <CampaignModal
                    campaign={editingCampaign}
                    onClose={() => { setShowModal(false); setEditingCampaign(null); }}
                    onSave={handleSaveCampaign}
                />
            )}
        </div>
    );
};

export default FollowupManagement;
